<script lang="ts">
export interface HeaderItem {
  name: string,
  key: string,
  link?: string,
  permission?: keyof NonNullable<GetAclResponse['payload']['permissions']['someStringKey']>,
  sectionBorder?: boolean,
  notifications: HeaderItemNotification[],
  showCondition?: (item: HeaderItem) => boolean,
  sections: HeaderItemSection[],
};

export interface HeaderItemSection {
  title?: string;
  permission?: keyof NonNullable<GetAclResponse['payload']['permissions']['someStringKey']>;
  subItems: HeaderSubitem[];
}

export interface HeaderSubitem {
  name: string,
  icon?: string,
  link: string,
  notifications: HeaderItemNotification[],
  permission?: keyof NonNullable<GetAclResponse['payload']['permissions']['someStringKey']>;
  subPermission?: string;
}

export interface HeaderItemNotification {
  count: number,
  type: HeaderItemNotificationType,
}

export type HeaderItemNotificationType  = 'positive' | 'negative' | 'warning' | 'neutral';
</script>

<script setup lang="ts">
//#region imports
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { find } from 'lodash-es';

import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import changeLocaleFork from '@/helpers/changeLocaleFork';

import HeaderNotifications from '@/components/HeaderNotifications.vue';
import SensitiveCheck from '@/components/sensitiveCheck.vue';

import eventBus from '@/setup/eventBus';
import { useApi } from '@/api';

import router from '@/router';
import { I18N_SET_LANG } from '@/store/action_types/i18n';
import { AUTH_2FA_SUCCESS, AUTH_CURRENT_TEAM, AUTH_LOGOUT, AUTH_SUCCESS } from '@/store/action_types/auth';
import { useTypedStore } from '@/store';
import { bootstrapApp } from '@/setup/bootstrap';
import { version } from '../../package.json';
import type { GetAclResponse } from '@/types/openapi-ts';
import type { AvaliableLocales } from '@/i18n';
//#endregion

//#region data
const store = useTypedStore();
const $i18n = useI18n();
const route = useRoute();
const { t } = useI18n();
const api = useApi();
// const isFirefoxRef = ref(isFirefox);
// const detectOSRef = ref(detectOS);
const versionRef = ref(version);
const confirm = ref();
//#endregion

//#region computed
const pDashboards = computed(() => store.getters['dashboards/dashboards']);
const pOverviewDashboard = computed(() => store.getters['dashboards/overviewDashboard']);

const headerLogoParams = computed(() => {
  return {
    url: global.theme.logo,
  };
});

const configLanguage = computed(() => {
  return [
    {
      code: 'en' as AvaliableLocales,
      title: t('theHeader.language.en'),
      image: 'us',
    },
    {
      code: 'ru' as AvaliableLocales,
      title: t('theHeader.language.ru'),
      image: 'ru',
    },
  ];
});

const configAffiliateProgram = computed(() => {
  return [
    ...(adminAcl.value.is_superuser ? [
      {
        title: t('theHeader.menu.settings.currencies'),
        code: 'currencies',
        route: '/settings/currencies',
      },
      {
        title: t('theHeader.menu.settings.fees'),
        code: 'fees',
        route: '/settings/fees',
      },
      {
        title: t('theHeader.menu.settings.offersCountryGroups'),
        code: 'offersCountryGroups',
        route: '/settings/offers-country-groups',
      },
      {
        title: t('theHeader.menu.settings.paymentMethod'),
        code: 'paymentMethods',
        route: '/settings/payment-methods',
      },
      {
        title: t('theHeader.menu.settings.predefinedPage'),
        code: 'predefinedPage',
        route: '/settings/predefined-page',
      },
    ] : []),
    {
      title: t('theHeader.menu.settings.domains'),
      code: 'domains',
      route: '/settings/sites',
    },
    ...(adminAcl.value.is_superuser ? [
      {
        title: t('theHeader.menu.settings.systemDomains'),
        code: 'systemDomains',
        route: '/settings/system-domains',
      },
      {
        title: t('theHeader.menu.settings.systemSettings'),
        code: 'systemSettings',
        route: '/settings/system-settings',
      },
      {
        title: t('settings.tokens.title'),
        code: 'tokenManagement',
        route: '/settings/token-management',
      },
      {
        title: t('theHeader.menu.settings.mediaBuying'),
        code: 'mediaBuying',
        route: '/settings/media-buying',
      },
    ] : []),
  ];
});

const lockStatus = computed(() => {
  return typeof store.getters['session/significantActionsSeconds']
  !== 'number'
  || store.getters['session/significantActionsSeconds'] === 0
    ? 'lock'
    : 'unlock';
});

const locale = computed(() => {
  return $i18n.locale.value;
});

const adminAcl = computed(() => {
  return store.getters['auth/adminAcl'];
});

const adminTeams = computed(() => {
  return adminAcl.value.teams || [];
});

const adminInfo = computed(() => {
  return store.getters['auth/adminInfo'];
});

const currentAcl = computed(() => {
  return store.getters['auth/currentAcl'];
});

const canViewAffiliates = computed(() => {
  return adminAcl.value.is_superuser || (currentAcl.value && currentAcl.value.affiliates_view === 'allow');
});

const crmAllow = computed(() => {
  return (store.getters['auth/currentAcl']!.crm_segments_view && store.getters['auth/currentAcl']!.crm_segments_view !== 'deny')
    || (store.getters['auth/currentAcl']!.crm_activities_view && store.getters['auth/currentAcl']!.crm_activities_view !== 'deny');
});

const currentTeam = computed(() => {
  if (adminTeams.value && store.getters['auth/currentTeam']) {
    const team = find(
      adminTeams.value, ['id', store.getters['auth/currentTeam']],
    );
    return team || { name: t('theHeader.profile.all_teams') };
  }
  return { name: t('theHeader.profile.all_teams') };
});

const currentRoute = computed<string>(() => {
  return store.getters['nav/getMainView'];
});

const mainMenu = computed<HeaderItem[]>(() => {
  const overviewDashboard = pOverviewDashboard.value.map((select) => ({
    name: select.name,
    icon: 'home',
    link: '/dashboards/overview',
    notifications: [],
  }));

  const dashboards = pDashboards.value.map((select) => ({
    name: select.name,
    link: `/dashboards/view/${select.id}`,
    notifications: [],
  }));

  const result: HeaderItem[] = [
    {
      name: t('theHeader.menu.dashboards.main'),
      key: 'dashboards',
      link: '/dashboards',
      permission: 'reports_view',
      sectionBorder: true,
      notifications: [],
      sections: [
        {
          title: '',
          subItems: overviewDashboard,
        },
        {
          title: '',
          subItems: dashboards,
        },
        {
          title: '',
          subItems: [
            {
              name: t('theHeader.menu.dashboards.add_dashboard'),
              icon: 'plus',
              link: '/dashboards/add-dashboard',
              notifications: [],
            },
          ],
        },
      ],
    },
    // {
    //   name: t('theHeader.menu.live.main'),
    //   key: 'live',
    //   link: '/live',
    //   notifications: [],
    //   sections: [
    //     {
    //       subItems: [
    //         {
    //           name: t('theHeader.menu.live.last-bets'),
    //           link: '/live/last-bets',
    //           notifications: [],
    //           permission: 'last_bets_and_deposits_view',
    //         },
    //         {
    //           name: t('theHeader.menu.live.top-events'),
    //           link: '/live/top-events',
    //           notifications: [],
    //           permission: 'top_events_view',
    //         },
    //       ],
    //     },
    //   ],
    // },
    {
      name: t('theHeader.menu.reports.main'),
      key: 'reports',
      link: '/reports/custom',
      permission: 'reports_view',
      notifications: [],
      sections: [
        {
          subItems: [
            {
              name: t('theHeader.menu.reports.report'),
              link: '/reports/custom',
              notifications: [],
            },
            {
              name: t('theHeader.menu.reports.cohort'),
              link: '/reports/cohort',
              notifications: [],
            },
          ],
        },
      ],
    },
    {
      name: t('theHeader.menu.affiliates.main'),
      key: 'affiliates',
      link: '/affiliates/list',
      permission: 'affiliates_view',
      notifications: [
        {
          count: store.getters['session/affiliatesTotal'],
          type: 'positive',
        },
        {
          count: store.getters['session/affiliatesMonitoringExpired'],
          type: 'negative',
        },
      ],
      sections: [
        {
          subItems: [
            {
              name: t('theHeader.menu.affiliates.list'),
              link: '/affiliates/list',
              notifications: [],
            },
            {
              name: t('theHeader.menu.affiliates.review'),
              link: '/affiliates/review',
              notifications: [
                {
                  count: store.getters['session/affiliatesReviews'],
                  type: 'positive',
                },
              ],
            },
            {
              name: t('theHeader.menu.affiliates.offers'),
              link: '/affiliates/offers/all',
              notifications: [],
            },
            {
              name: t('theHeader.menu.affiliates.monitored'),
              link: '/affiliates/monitored',
              notifications: [
                {
                  count: store.getters['session/affiliatesMonitoringExpired'],
                  type: 'negative',
                },
              ],
            },
            {
              name: t('theHeader.menu.affiliates.notifications'),
              link: '/affiliates/notification',
              notifications: [],
              permission: 'affiliates_notifications_view',
            },
            {
              name: t('theHeader.menu.affiliates.kpiBonuses'),
              link: '/affiliates/kpi-bonus',
              notifications: [],
            },
          ],
        },
      ],
    },
    {
      name: t('theHeader.menu.payments.main'),
      key: 'payments',
      link: '/payments',
      permission: 'payments_view',
      notifications: [
        {
          count: store.getters['session/paymentsReviews'],
          type: 'positive',
        },
        {
          count: store.getters['session/paymentsForRework'],
          type: 'negative',
        },
        {
          count: store.getters['session/paymentsNotPaid'],
          type: 'warning',
        },
      ],
      sections: [
        {
          subItems: [
            {
              name: t('theHeader.menu.payments.dashboard'),
              link: '/payments/dashboard',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.payments.payment_moderation_section'),
          subItems: [
            {
              name: t('theHeader.menu.payments.on_review'),
              link: '/payments/moderation/on_review',
              notifications: [
                {
                  count: store.getters['session/paymentsReviews'],
                  type: 'positive',
                },
              ],
            },
            {
              name: t('theHeader.menu.payments.approved'),
              link: '/payments/moderation/approved',
              notifications: [],
            },
            {
              name: t('theHeader.menu.payments.declined'),
              link: '/payments/moderation/declined',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.payments.payment_payout_section'),
          subItems: [
            {
              name: t('theHeader.menu.payments.not_paid'),
              link: '/payments/payout/not_paid',
              notifications: [
                {
                  count: store.getters['session/paymentsNotPaid'],
                  type: 'warning',
                },
              ],
            },
            {
              name: t('theHeader.menu.payments.for_rework'),
              link: '/payments/payout/for_rework',
              notifications: [
                {
                  count: store.getters['session/paymentsForRework'],
                  type: 'negative',
                },
              ],
            },
            {
              name: t('theHeader.menu.payments.processing'),
              link: '/payments/payout/processing',
              notifications: [],
            },
            {
              name: t('theHeader.menu.payments.paid'),
              link: '/payments/payout/paid',
              notifications: [],
            },
            {
              name: t('theHeader.menu.payments.failed'),
              link: '/payments/payout/failed',
              notifications: [],
            },
            {
              name: t('theHeader.menu.payments.external'),
              link: '/payments/external',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.payments.payment_cpa_section'),
          subItems: [
            {
              name: t('theHeader.menu.payments.cpa_conversions'),
              link: '/payments/cpa-conversions',
              notifications: [],
            },
          ],
        },
      ],
    },
    {
      name: t('theHeader.menu.players.main'),
      key: 'players',
      link: '/players',
      permission: 'players_view',
      showCondition: (/* item */) => adminAcl.value.is_superuser
        || store.getters['auth/currentAcl']!.players_view === 'allow',
      notifications: [],
      sections: [],
    },
    {
      name: t('theHeader.menu.tools.main'),
      key: 'tools',
      link: '/tools',
      notifications: [],
      sections: [
        // {
        //   title: t('theHeader.menu.tools.promo-codes'),
        //   subItems: [
        //     {
        //       name: t('theHeader.menu.tools.promoChecker'),
        //       link: '/tools/promo-checker',
        //       notifications: [],
        //       permission: 'tools_promo_codes_checker_view',
        //     },
        //   ],
        // },
        {
          title: t('theHeader.menu.tools.dms'),
          subItems: [
            {
              name: t('theHeader.menu.tools.rotation_groups'),
              link: '/tools/rotation-groups',
              notifications: [],
              permission: 'service_rotation_groups_view',
            },
            {
              name: t('theHeader.menu.tools.service_domains'),
              link: '/tools/service-domains',
              notifications: [],
              permission: 'platform_service_domains_view',
            },
            {
              name: t('theHeader.menu.tools.servers'),
              link: '/tools/servers',
              notifications: [],
              permission: 'platform_servers_view',
            },
            {
              name: t('theHeader.menu.tools.dns_hosting_accounts'),
              link: '/tools/dns-hosting-accounts',
              notifications: [],
              permission: 'dns_hosting_accounts_view',
            },
            {
              name: t('theHeader.menu.tools.domain_registrar_accounts'),
              link: '/tools/domain-registrar-accounts',
              notifications: [],
              permission: 'domain_registrar_accounts_view',
            },
            {
              name: t('theHeader.menu.tools.dedicated_site_domains'),
              link: '/tools/dedicated-site-domains',
              notifications: [],
              permission: 'platform_betting_domains_view',
            },
          ],
        },
        {
          title: t('theHeader.menu.tools.promo-materials'),
          subItems: [
            {
              name: t('theHeader.menu.tools.marketing-materials'),
              link: '/tools/marketing-materials',
              permission: 'marketing_materials_view',
              notifications: [],
            },
            {
              name: t('theHeader.menu.tools.landingLeagues'),
              link: '/tools/landing-leagues',
              notifications: [],
              permission: 'tools_promo_landings_view',
            },
          ],
        },
      ],
    },
    {
      name: t('theHeader.menu.teams.main'),
      key: 'teams',
      link: '/teams',
      //@ts-expect-error такого значение нет в пермишенах
      permission: 'teams_view',
      showCondition: (/* item */) => adminAcl.value.is_superuser,
      notifications: [],
      sections: [],
    },
    {
      name: t('theHeader.menu.media-buying.main'),
      key: 'media-buying',
      notifications: [
        {
          count:
            store.getters['session/mediaBuyingPaymentsModerationReviews']
            + store.getters['session/mediaBuyingPaymentsPayoutNotPaid']
            + store.getters['session/mediaBuyingPostsReviews'],
          type: 'negative',
        },
        {
          type: 'warning',
          count:
            store.getters['session/mediaBuyingPaymentsModerationRework']
            + store.getters['session/mediaBuyingPaymentsPayoutRework']
            + store.getters['session/mediaBuyingPostsRework'],
        },
        {
          type: 'neutral',
          count: store.getters['session/mediaBuyingPaymentsPayoutProcessing'],
        },
      ],
      showCondition: (/* item */) => adminAcl.value.is_superuser,
      sections: [
        {
          subItems: [
            {
              name: t('theHeader.menu.media-buying.blacklist'),
              link: '/media-buying/blacklist',
              notifications: [],
              permission: 'media_buying_payment_methods_blacklist_view',
            },
            {
              name: t('theHeader.menu.media-buying.blacklist_urls'),
              link: '/media-buying/blacklist-urls',
              notifications: [],
              permission: 'media_buying_payment_methods_blacklist_view',
            },
            {
              name: t('theHeader.menu.media-buying.transfer'),
              link: '/media-buying/transfer',
              permission: 'media_buying_payments_view',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.media-buying.reports_section'),
          subItems: [
            {
              name: t('theHeader.menu.media-buying.summary_reports'),
              link: '/media-buying/reports',
              permission: 'media_buying_reports_view',
              notifications: [],
            },
            {
              name: t('theHeader.menu.media-buying.unreported_funds'),
              link: '/media-buying/unreported-funds',
              permission: 'media_buying_reports_view',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.media-buying.payment_moderation_section'),
          permission: 'media_buying_payments_view',
          subItems: [
            {
              name: t('theHeader.menu.media-buying.on_review'),
              permission: 'media_buying_payments_moderation_statuses_visible',
              subPermission: 'on_review',
              link: '/media-buying/payments-moderation/on-review',
              notifications: [
                {
                  count: store.getters['session/mediaBuyingPaymentsModerationReviews'],
                  type: 'negative',
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.for_rework'),
              permission:'media_buying_payments_moderation_statuses_visible',
              subPermission: 'for_rework',
              link: '/media-buying/payments-moderation/for-rework',
              notifications: [
                {
                  type: 'warning',
                  count: store.getters['session/mediaBuyingPaymentsModerationRework'],
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.approved'),
              permission:'media_buying_payments_moderation_statuses_visible',
              subPermission: 'approved',
              link: '/media-buying/payments-moderation/approved',
              notifications: [],
            },
            {
              name: t('theHeader.menu.media-buying.declined'),
              permission:'media_buying_payments_moderation_statuses_visible',
              subPermission: 'declined',
              link: '/media-buying/payments-moderation/declined',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.media-buying.payment_payout_section'),
          permission: 'media_buying_payments_view',
          subItems: [
            {
              name: t('theHeader.menu.media-buying.not_paid'),
              permission: 'media_buying_payments_payout_statuses_visible',
              subPermission: 'not_paid',
              link: '/media-buying/payments-payout/not-paid',
              notifications: [
                {
                  count: store.getters['session/mediaBuyingPaymentsPayoutNotPaid'],
                  type: 'negative',
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.for_rework'),
              permission: 'media_buying_payments_payout_statuses_visible',
              subPermission: 'for_rework',
              link: '/media-buying/payments-payout/for-rework',
              notifications: [
                {
                  type: 'warning',
                  count: store.getters['session/mediaBuyingPaymentsPayoutRework' ],
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.processing'),
              permission: 'media_buying_payments_payout_statuses_visible',
              subPermission: 'processing',
              link: '/media-buying/payments-payout/processing',
              notifications: [
                {
                  type: 'neutral',
                  count: store.getters['session/mediaBuyingPaymentsPayoutProcessing'],
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.paid_out'),
              permission: 'media_buying_payments_payout_statuses_visible',
              subPermission: 'paid',
              link: '/media-buying/payments-payout/paid',
              notifications: [],
            },
            {
              name: t('theHeader.menu.media-buying.failed'),
              permission: 'media_buying_payments_payout_statuses_visible',
              subPermission: 'failed',
              link: '/media-buying/payments-payout/failed',
              notifications: [],
            },
          ],
        },
        {
          title: t('theHeader.menu.media-buying.posts_section'),
          permission: 'media_buying_posts_view',
          subItems: [
            {
              name: t('theHeader.menu.media-buying.on_review'),
              permission: 'media_buying_posts_statuses_visible',
              subPermission: 'on_review',
              link: '/media-buying/posts/on-review',
              notifications: [
                {
                  count: store.getters['session/mediaBuyingPostsReviews'],
                  type: 'negative',
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.for_rework'),
              permission: 'media_buying_posts_statuses_visible',
              subPermission: 'for_rework',
              link: '/media-buying/posts/for-rework',
              notifications: [
                {
                  type: 'warning',
                  count: store.getters['session/mediaBuyingPostsRework'],
                },
              ],
            },
            {
              name: t('theHeader.menu.media-buying.approved'),
              permission: 'media_buying_posts_statuses_visible',
              subPermission: 'approved',
              link: '/media-buying/posts/approved',
              notifications: [],
            },
            {
              name: t('theHeader.menu.media-buying.declined'),
              permission: 'media_buying_posts_statuses_visible',
              subPermission: 'declined',
              link: '/media-buying/posts/declined',
              notifications: [],
            },
          ],
        },
      ],
    },
    {
      name: t('theHeader.menu.crm.main'),
      key: 'crm',
      link: '/crm',
      showCondition: (/* item */) => adminAcl.value.is_superuser || crmAllow.value,
      notifications: [],
      sections: [],
    },
  ];

  return result;
});

const mainMenuFiltered = computed(() => {
  return mainMenu.value
    .filter((item) => {
        const commonCondition = ((!item.permission
        || store.getters['auth/currentAcl']![item.permission] === 'allow')
          && !adminAcl.value.is_superuser)
        || adminAcl.value.is_superuser;
      const alwaysShowCondition = item.showCondition && item.showCondition(item);
      return commonCondition || alwaysShowCondition;
    })
    .map((item) => ({
      ...item,
      sections: item.sections
        .filter(
          (section) => ((!section.permission
            || store.getters['auth/currentAcl']![section.permission] === 'allow')
              && !adminAcl.value.is_superuser)
            || adminAcl.value.is_superuser,
        )
        .map((section) => ({
          ...section,
          subItems: section.subItems.filter((subItem) => {
            const isArray = subItem.permission && Array.isArray(store.getters['auth/currentAcl']![subItem.permission]);
            let isIncludeItem = false;
            if (subItem.permission && isArray) {
              const permissionArray: any = store.getters['auth/currentAcl']![subItem.permission];
              const permissionArrayItem = subItem.subPermission;
              isIncludeItem = permissionArray.includes(permissionArrayItem);
            }
            return (
              adminAcl.value.is_superuser
              || !subItem.permission
              || store.getters['auth/currentAcl']![subItem.permission]
              === 'allow'
              || (isArray && isIncludeItem)
            );
          }),
        }))
        .filter((section) => section.subItems.length > 0),
    }))
    .filter((item) => {
      const alwaysShowCondition = item.showCondition && item.showCondition(item);
      const subSectionsCount = item.sections
        .map((section) => section.subItems.length)
        .reduce((a, b) => a + b, 0);
      return subSectionsCount > 0 || alwaysShowCondition;
    });
});
//#endregion
onMounted(() => {
  eventBus.$on('lock', lock);
  eventBus.$on('teamsRoute', teamsRoute);
  eventBus.$on('tokenManagementRoute', tokenManagementRoute);
  eventBus.$on('feesRoute', feesRoute);
  eventBus.$on('paymentMethodsRoute', paymentMethodsRoute);
  eventBus.$on('systemSettings', systemSettings);
  eventBus.$on('systemDomains', systemDomains);
  eventBus.$on('predefinedPage', predefinedPage);
  eventBus.$on('offersCountryGroups', offersCountryGroups);
  eventBus.$on('currenciesRoute', currenciesRoute);
  eventBus.$on('mediaBuyingRoute', mediaBuyingPaymentMethodsRoute);
});

onBeforeUnmount(() => {
  eventBus.$off('lock', lock);
  eventBus.$off('teamsRoute', teamsRoute);
  eventBus.$off('tokenManagementRoute', tokenManagementRoute);
  eventBus.$off('feesRoute', feesRoute);
  eventBus.$off('paymentMethodsRoute', paymentMethodsRoute);
  eventBus.$off('systemSettings', systemSettings);
  eventBus.$off('systemDomains', systemDomains);
  eventBus.$off('predefinedPage', predefinedPage);
  eventBus.$off('offersCountryGroups', offersCountryGroups);
  eventBus.$off('currenciesRoute', currenciesRoute);
  eventBus.$off('mediaBuyingRoute', mediaBuyingPaymentMethodsRoute);
});
//#region LiveCycle

//#endregion

//#region methods
const isActive = (key: string) => {
  return currentRoute.value === key;
};

const getSetting = async () => {
  await store.dispatch('reports/getSettings');
  eventBus.$emit('changeLocale');
};

const setLanguage = async (lang: AvaliableLocales) => {
  changeLocaleFork(lang);
  await store.dispatch(`i18n/${I18N_SET_LANG}`, lang);
  api.postLanguage(lang);

  if (route.path !== '/reports/custom') {
    /* TODO: поправить timeout */
    setTimeout(getSetting);
  }
  if (route.path === '/reports/custom') {
    eventBus.$emit('changeLocale', lang);
  }
  setTimeout(() => eventBus.$emit('changeLocaleGlobal', lang));
};

const changeTeam = async (id?: string) => {
  localStorage.setItem('active-team', id || '');

  await reloadApp();
};

const reloadApp = async () => {
  store.commit(`auth/${AUTH_CURRENT_TEAM}`, localStorage.getItem('active-team')!);

  try {
    await bootstrapApp({ force: true });
  } catch {
    return;
  }

  if (route.path === '/affiliates/list' && !canViewAffiliates.value) {
    await router.push('/profile');
  }

  store.commit(`auth/${AUTH_SUCCESS}`);
  store.commit(`auth/${AUTH_2FA_SUCCESS}`);
  store.commit('other/uuidRender');
};

const checkSensitiveAction = (action: string) => {
  eventBus.$emit('checkSensitiveAction', action);
};

const lock = async () => {
  await store.dispatch('session/getSessionAuth');
  await api.significantLock();
  await store.dispatch('session/lockSession');
};

const teamsRoute = () => {
  router.push('/teams');
};

const tokenManagementRoute = () => {
  router.push('/settings/token-management');
};

const predefinedPage = () => {
  router.push('/settings/predefined-page');
};

const offersCountryGroups = () => {
  router.push('/settings/offers-country-groups');
};

const currenciesRoute = () => {
  router.push('/settings/currencies');
};

const feesRoute = () => {
  router.push('/settings/fees');
};

const paymentMethodsRoute = () => {
  router.push('/settings/payment-methods');
};

const systemSettings = () => {
  router.push('/settings/system-settings');
};

const systemDomains = () => {
  router.push('/settings/system-domains');
};

const mediaBuyingPaymentMethodsRoute = () => {
  router.push('/settings/media-buying');
};

const confirmSignOut = () => {
  confirm.value.open();
};

const signOut = () => {
  store.dispatch('session/clearTimers');
  store
    .dispatch(`auth/${AUTH_LOGOUT}`)
    .then(() => {
      router.push({
        name: 'login',
        params: {
          action: 'auth',
        },
      });
    })
    .catch((error: any) => {
      console.log(error.data.errors);
      // this.errors = cloneDeepWith(error.data.errors);
    });
};
//#endregion
</script>

<template>
  <div class="the-header">
    <UiConfirm
      ref="confirm"
      :width="480"
      type="warning"
      :action-name="t('theHeader.confirm.action')"
      action="Sign Out"
      action-fa-icon="sign-out"
      @save="signOut"
    >
      <template v-slot:title>
        <div>
          {{ t('theHeader.confirm.title') }}
        </div>
      </template>

      <div class="body__wrap">
        <i
          class="fas warning fa-exclamation-triangle t:mr-12"
        />
        <div
          class="body__msg"
          v-html="t('theHeader.confirm.msg')"
        />
      </div>
    </UiConfirm>
    <SensitiveCheck />
    <div class="the-header__content">
      <a
        class="the-header__logo"
        @click="reloadApp()"
      >
        <img
          :src="headerLogoParams.url"
          alt="the-header-logo"
          class="the-header__logo-img"
        >
      </a>
      <div class="the-header__menu">
        <div
          v-for="item in mainMenuFiltered"
          :key="item.name"
          :class="[
            'the-header__menu-item',
            {'the-header__menu-item_active': isActive(item.key)}
          ]"
        >
          <HeaderNotifications
            v-if="item.notifications.length > 0"
            :list="item.notifications"
          />
          <component
            :is="item.link ? 'RouterLink' : 'div'"
            :title="item.name"
            class="the-header__menu-item-link"
            :to="item.link"
          >
            {{ item.name }}
          </component>
          <UiIcon
            v-if="item.sections.length > 0"
            class="t:ml-4"
            color="inherit"
            name="chevron-down"
          />
          <div class="the-header__menu-pop">
            <UiScrollbar
              wrap-style="max-height: 470px;"
              :native="false"
            >
              <UiScrollbar
                v-for="(section, index) in item.sections"
                :key="`${section.title}${index}`"
                wrap-style="max-height: 400px;"
                :native="false"
                :class="[
                  'the-header__menu-pop-section',
                  {'the-header__menu-pop-section_border': item.sectionBorder }
                ]"
              >
                <div
                  v-if="section.title"
                  class="the-header__menu-pop-section-title"
                >
                  {{ section.title }}
                </div>
                <RouterLink
                  v-for="(sublink, index) in section.subItems"
                  :key="sublink.name + index"
                  :title="sublink.name"
                  class="the-header__menu-pop-section-link"
                  :to="sublink.link"
                >
                  <UiIcon
                    v-if="sublink.icon"
                    class="t:mr-4"
                    color="inherit"
                    :name="sublink.icon"
                  />
                  {{ sublink.name }}
                  <HeaderNotifications
                    v-if="sublink.notifications.length > 0"
                    :list="sublink.notifications"
                    class="the-header__menu-pop-section-notifications"
                  />
                </RouterLink>
              </UiScrollbar>
            </UiScrollbar>
          </div>
        </div>
      </div>
      <div class="the-header__user">
        <div
          :class="[
            'the-header__menu-item the-header__menu-item_user',
            {'the-header__menu-item_active': isActive('settings')}
          ]"
        >
          <UiIcon
            :size="16"
            color="inherit"
            name="cog"
          />
          <UiIcon
            class="t:ml-8"
            color="inherit"
            name="chevron-down"
          />
          <div class="the-header__menu-pop">
            <div class="the-header__menu-pop-section">
              <div class="the-header__menu-pop-section-title">
                {{ t('theHeader.menu.settings.affiliateProgram') }}
              </div>
              <RouterLink
                v-for="affiliateProgram in configAffiliateProgram"
                :key="affiliateProgram.code + affiliateProgram.title"
                class="the-header__menu-pop-section-link"
                :to="affiliateProgram.route"
              >
                {{ affiliateProgram.title }}
              </RouterLink>
            </div>
            <div class="the-header__menu-pop-section">
              <div class="the-header__menu-pop-section-title">
                {{ t('theHeader.language.language') }}
              </div>
              <div
                v-for="language in configLanguage"
                :key="language.code"
                :class="[
                  'the-header__menu-pop-section-link',
                  { 'the-header__menu-pop-section-link_active': language.code === locale }
                ]"
                @click="setLanguage(language.code)"
              >
                <Flag
                  class="the-header__user-locale-icon"
                  :iso="language.image"
                />
                {{ language.title }}
              </div>
            </div>
            <div class="the-header__menu-pop-section">
              <div class="the-header__menu-pop-section-title">
                v{{ versionRef }}
              </div>
            </div>
          </div>
        </div>
        <div
          class="the-header__menu-item the-header__menu-item_user the-header__menu-item_lock"
          @click="checkSensitiveAction(lockStatus === 'unlock' ? 'lock' : 'unlock')"
        >
          <UiIcon
            :size="16"
            color="inherit"
            :name="`lock${lockStatus === 'lock' ? '' : '-open'}-alt`"
          />
        </div>
        <div
          :class="[
            'the-header__menu-item the-header__menu-item_user',
            {'the-header__menu-item_active': isActive('profile')}
          ]"
        >
          <div class="the-header__user-profile">
            {{ adminInfo.name }}
            <div
              v-if="('id' in currentTeam && !adminAcl.is_superuser) || adminAcl.is_superuser"
              class="the-header__user-profile-team"
            >
              <UiIcon
                class="t:mr-4"
                color="inherit"
                name="users"
              />
              <span class="the-header__user-profile-team-name">
                {{ currentTeam.name }}
              </span>
            </div>
          </div>
          <UiIcon
            class="t:ml-8"
            color="inherit"
            name="chevron-down"
          />
          <div class="the-header__menu-pop">
            <div class="the-header__menu-pop-section the-header__menu-pop-section_border">
              <RouterLink
                class="the-header__menu-pop-section-link"
                to="/profile"
              >
                <UiIcon
                  class="t:mr-4"
                  color="inherit"
                  name="user"
                />
                {{ t('theHeader.profile.main') }}
              </RouterLink>
            </div>
            <div class="the-header__menu-pop-section the-header__menu-pop-section_border">
              <div
                v-if="adminAcl.is_superuser"
                :class="[
                  'the-header__menu-pop-section-link',
                  { 'the-header__menu-pop-section-link_active': !('id' in currentTeam) }
                ]"
                @click="changeTeam()"
              >
                {{ t('theHeader.profile.all_teams') }}
              </div>
              <UiScrollbar
                wrap-style="max-height: 380px;"
                :native="false"
                class="the-header__menu-pop-section the-header__menu-pop-section_border"
              >
                <div
                  v-for="(team, index) in adminTeams"
                  :key="index"
                  :class="[
                    'the-header__menu-pop-section-link',
                    { 'the-header__menu-pop-section-link_active': 'id' in currentTeam && team.id === currentTeam.id }
                  ]"
                  @click="changeTeam(team.id)"
                >
                  {{ team.name }}
                </div>
              </UiScrollbar>
            </div>
            <div class="the-header__menu-pop-section the-header__menu-pop-section_border">
              <div
                class="the-header__menu-pop-section-link"
                @click="confirmSignOut"
              >
                <UiIcon
                  class="t:mr-4"
                  color="inherit"
                  name="sign-out"
                />
                {{ t('theHeader.profile.logout') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.the-header {
  width: 100%;
  height: 54px;
  min-height: 54px;
  user-select: none;
  background-color: var(--header-menu);

  &__content {
    width: 90%;
    height: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 1140px;
  }

  &__logo {
    cursor: pointer;
  }
  &__logo-img {
    max-height: var(--max-logo-height);
  }

  &__menu {
    display: flex;
    height: 100%;
  }
  &__menu-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0 8px;
    color: var(--header-text);

    &_user {
      padding: 0 12px;
      border-left: 1px solid var(--header-divider);
      &:last-child {
        border-right: 1px solid var(--header-divider);
      }
    }
    &_lock {
      width: 34px;
    }

    &_active {
      color: var(--header-text-active);
    }
    &_active,
    &:hover {
      background-color: var(--overlay-black-20);
      box-shadow: inset 0 -2px 0 0 var(--border-accent);
    }
  }
  &__menu-item_user &__menu-pop{
    left: initial;
    right: 0;
  }
  &__menu-item:hover &__menu-pop{
    display: block;
  }
  &__menu-item-link{
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 400;
    text-decoration: none;
  }
  &__menu-pop {
    display: none;
    position: absolute;
    //z-index: 9999;
    z-index: 890;
    left: 0;
    background-color: var(--header-submenu);
    min-width: 120px;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.16);
    top: 100%;
  }
  &__menu-pop-section{
    background: var(--header-menu);
    &_border {
      border-bottom: 1px solid var(--header-divider);
      &:last-child {
        border-bottom: none;
      }
    }
  }
  &__menu-pop-section-title{
    height: 34px;
    display: flex;
    align-items: center;
    color: var(--text-body-invert);
    font-weight: 500;
    font-size: 12px;
    text-transform: uppercase;
    white-space: nowrap;
    padding-left: 8px;
    padding-right: 32px;
    opacity: .8;
    cursor: default;
  }
  &__menu-pop-section-link{
    color: var(--text-body-invert);
    font-weight: 400;
    font-size: 12px;
    white-space: nowrap;
    padding-left: 8px;
    padding-right: 32px;
    display: flex;
    align-items: center;
    background-color: var(--header-submenu);
    position: relative;
    height: 34px;

    &_active,
    &.router-link-active {
      color: var(--header-text-active);



    }
    &:hover {
      background: var(--header-menu);
    }
  }
  &__menu-pop-section-notifications{
    position: relative;
    left: initial;
    top: initial;
    right: initial;
    margin-left: 8px;
  }

  &__user {
    display: flex;
    height: 100%;
  }
  &__user-locale-icon {
    width: 12px;
    height: 12px;
    margin-right: 4px;
  }
  &__user-profile {
    font-size: 13px;
    text-align: right;
  }
  &__user-profile-team {
    display: flex;
    align-items: center;
    margin-top: 4px;
  }
  &__user-profile-team-name{
    font-weight: 700;
    font-size: 11px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 80px;
  }
}
</style>
